/**
 * Review preview — M6 §2 previews, shown inside the §5 review modal.
 *
 * Three previews behind one component, because the reviewer's question is the same in all
 * three cases ("is this the data it says it is?") and only the rendering differs:
 *
 * * **raster** — a Leaflet mini-map over the staging COG's TiTiler tiles
 *   (`checks_meta.preview_tiles`), framed on `checks_meta.extent`;
 * * **vector** — the same mini-map, drawing `GET /uploads/{id}/preview.geojson` (the
 *   validator's ≤ 2 000 simplified features), framed on what it drew;
 * * **table** — the first rows the validator sampled, under the columns it reported.
 *
 * The map is **created here**, not taken from `features/map`'s context: this one is a 220 px
 * inspection surface inside a dialog with no panes, no store and no permalink, and wiring it
 * to the portal's single map would mean the reviewer's preview moved the reader's map.
 *
 * The basemap under both map previews is the **streets** fallback row (`BASEMAPS.streets`,
 * OpenStreetMap) rather than the reader's chosen one or the served CARTO light: CARTO is keyed
 * per deployment and a keyless tile comes back stamped "API KEY REQUIRED", which is what a
 * reviewer would be looking through. OSM needs no key and no `/meta/config` round trip, and
 * this is a 240 px inspection frame, not a map the reader navigates.
 */
import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';

import { fetchUploadPreview, queryKeys, type UploadRow } from '../../lib/api';
import { BASEMAPS } from '../map/basemaps';
import { VECTOR_STYLE, leafletBounds, previewKind, previewTable, rowCount } from './manager';

export interface PreviewProps {
  row: UploadRow;
}

export function Preview({ row }: PreviewProps) {
  const { t } = useTranslation();
  const kind = previewKind(row);

  if (kind === 'raster') return <RasterPreview row={row} />;
  if (kind === 'vector') return <VectorPreview row={row} />;
  if (kind === 'table') return <TablePreview row={row} />;

  return (
    <p className="mgr-hint mgr-preview-none" data-testid="mgr-preview-none">
      {t('manager.rev.noPreview')}
    </p>
  );
}

/**
 * The map element and its lifetime, shared by both map previews.
 *
 * `draw` runs once the map exists and returns its own teardown, so each preview says only
 * what it puts on the map. It is held in a ref rather than listed as a dependency: a caller
 * that rebuilds the callback every render would otherwise tear the whole map down and build
 * it again on each keystroke in the comment box below it.
 */
function useMiniMap(draw: (map: L.Map) => void | (() => void), deps: readonly unknown[]) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const drawRef = useRef(draw);
  drawRef.current = draw;

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const map = L.map(el, {
      zoomControl: false,
      attributionControl: false,
      // A preview is looked at, not navigated: the reviewer's scroll belongs to the dialog.
      scrollWheelZoom: false,
      // `fitBounds` rounds *down* to a multiple of `zoomSnap`, which on a 240 px box throws
      // away up to a whole zoom level and frames three countries around the one uploaded.
      zoomSnap: 0,
    });
    L.tileLayer(BASEMAPS.streets.url, {
      maxZoom: BASEMAPS.streets.maxZoom,
    }).addTo(map);
    const undraw = drawRef.current(map);
    return () => {
      undraw?.();
      map.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return elRef;
}

function RasterPreview({ row }: PreviewProps) {
  const tiles = row.checks_meta.preview_tiles ?? '';
  const bounds = leafletBounds(row.checks_meta.extent);

  const elRef = useMiniMap(
    (map) => {
      L.tileLayer(tiles, { opacity: 1, maxZoom: 18 }).addTo(map);
      if (bounds) map.fitBounds(bounds);
      else map.setView([12.5, -1.5], 5);
    },
    [tiles, row.id],
  );

  return <div className="mgr-preview-map" data-testid="mgr-preview-map" ref={elRef} />;
}

function VectorPreview({ row }: PreviewProps) {
  const { t } = useTranslation();
  const { data, isError } = useQuery({
    queryKey: queryKeys.uploadPreview(row.id),
    queryFn: fetchUploadPreview,
  });
  const fallback = leafletBounds(row.checks_meta.extent);

  const elRef = useMiniMap(
    (map) => {
      if (!data) {
        if (fallback) map.fitBounds(fallback);
        else map.setView([12.5, -1.5], 5);
        return;
      }
      const layer = L.geoJSON(data, {
        style: () => VECTOR_STYLE,
        /*
         * Points as circle markers, not Leaflet's default `L.marker`.
         *
         * The default pin is an `<img>` at `/marker-icon.png` relative to the page, and this
         * bundle ships no such file — so a point layer previewed as markers is a grid of
         * broken-image glyphs. A `circleMarker` is drawn, not fetched, and it takes
         * `VECTOR_STYLE`, so points, lines and polygons read as one layer.
         */
        pointToLayer: (_feature, latlng) => L.circleMarker(latlng, { ...VECTOR_STYLE, radius: 4 }),
      }).addTo(map);
      // A collection Leaflet could not bound at all (every geometry null) leaves the map on
      // the declared extent rather than on Leaflet's own default view of the whole world.
      const drawn = layer.getBounds();
      if (drawn.isValid()) map.fitBounds(drawn);
      else if (fallback) map.fitBounds(fallback);
      else map.setView([12.5, -1.5], 5);
    },
    [data, row.id],
  );

  if (isError) {
    return (
      <p className="mgr-hint mgr-preview-none" data-testid="mgr-preview-error">
        {t('manager.rev.previewError')}
      </p>
    );
  }

  const count = row.checks_meta.feature_count;
  return (
    <div className="mgr-preview-vec">
      <div className="mgr-preview-map" data-testid="mgr-preview-map" ref={elRef} />
      {typeof count === 'number' ? (
        <p className="mgr-hint">{t('manager.rev.featuresNote', { n: count.toLocaleString() })}</p>
      ) : null}
    </div>
  );
}

function TablePreview({ row }: PreviewProps) {
  const { t } = useTranslation();
  const table = previewTable(row.checks_meta);
  if (!table) return null;
  const total = rowCount(row.checks_meta) ?? table.rows.length;

  return (
    <div className="mgr-preview-tbl">
      <div className="rows-wrap">
        <table className="mini rows-table" data-testid="mgr-preview-table">
          <thead>
            <tr>
              {table.columns.map((c) => (
                <th key={c} scope="col">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* The sample has no ids: rows are keyed by position, and the whole sample is
                replaced at once or not at all. */}
            {table.rows.map((cells, i) => (
              <tr key={i}>
                {cells.map((cell, j) => (
                  <td key={table.columns[j] ?? j}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mgr-hint">
        {t('manager.rev.rowsNote', {
          n: table.rows.length.toLocaleString(),
          total: total.toLocaleString(),
        })}
      </p>
    </div>
  );
}
